import type { FractionEquationVisual } from './teachingTimeline.ts';
export type InkPoint = readonly [number, number];
export type InkStroke = { points: InkPoint[]; length: number; role?: 'cancel' | 'identity' };
// Single-line pen glyphs on a 24 × 36 em. The paths are drawn, not font masks.
const glyphs: Record<string, readonly (readonly InkPoint[])[]> = {
  '0': [[[18,3],[9,1],[3,8],[2,25],[7,34],[16,34],[21,25],[22,10],[18,3]]],
  '1': [[[5,9],[13,2],[12,34]],[[5,35],[21,35]]],
  '2': [[[2,8],[8,2],[16,2],[22,8],[20,15],[3,33],[22,33]]],
  '3': [[[3,4],[15,2],[21,7],[19,14],[11,18],[19,20],[22,27],[18,33],[8,35],[2,31]]],
  '4': [[[17,35],[18,2],[2,24],[23,24]]],
  '5': [[[22,3],[5,3],[3,17],[14,16],[21,21],[21,29],[15,35],[6,34],[2,30]]],
  '6': [[[20,3],[12,2],[5,10],[2,24],[5,33],[14,35],[21,29],[20,21],[13,17],[4,20]]],
  '7': [[[2,3],[23,3],[9,35]],[[8,18],[19,18]]],
  '8': [[[12,18],[4,12],[4,5],[11,1],[19,4],[21,11],[12,18],[3,24],[3,31],[11,36],[20,32],[22,25],[12,18]]],
  '9': [[[20,17],[12,21],[4,17],[2,9],[7,2],[16,2],[21,8],[20,24],[15,33],[5,35]]],
  'x': [[[3,12],[20,34]],[[21,11],[2,35]]],
  '+': [[[2,20],[22,20]],[[12,10],[12,30]]],
  '-': [[[2,20],[22,20]]], '−': [[[2,20],[22,20]]],
  '=': [[[2,15],[22,15]],[[2,25],[22,25]]],
  '≠': [[[2,15],[22,15]],[[2,25],[22,25]],[[19,7],[5,33]]],
  '×': [[[3,10],[21,30]],[[21,10],[3,30]]],
  '÷': [[[2,20],[22,20]],[[12,8],[12,9]],[[12,31],[12,32]]],
  '/': [[[22,1],[2,36]]],
  '(': [[[17,0],[10,8],[7,19],[10,29],[17,38]]],
  ')': [[[6,0],[13,8],[16,19],[13,29],[6,38]]],
  '.': [[[12,34],[12,35]]],
  '?': [[[3,8],[6,3],[14,1],[21,6],[21,12],[13,19],[12,25]],[[12,34],[12,35]]],
};
export function inkLine(text: string) {
  let x = 0;
  const strokes: InkStroke[] = [];
  for (const char of text) {
    if (char === ' ') { x += 13; continue; }
    for (const points of glyphs[char] ?? []) {
      const placed: InkPoint[] = points.map(([px, py]) => [x + px, py]);
      const length = placed.slice(1).reduce((sum, point, i) => sum + Math.hypot(point[0] - placed[i][0], point[1] - placed[i][1]), 0);
      strokes.push({ points: placed, length });
    }
    x += 29;
  }
  return { strokes, width: Math.max(1, x), height:38, length: strokes.reduce((sum,s) => sum+s.length,0) };
}

/** True numerator/bar/denominator geometry, shared by rendering and regression tests. */
export function inkFractionEquation(visual: FractionEquationVisual) {
  const strokes:InkStroke[]=[],cancellations:InkStroke[]=[],identities:InkStroke[]=[];
  const placed=(text:string,x:number,y:number,role?:InkStroke['role'])=>inkLine(text).strokes.map(s=>({...s,points:s.points.map(([px,py])=>[x+px,y+py] as InkPoint),role}));
  const segment=(a:InkPoint,b:InkPoint,role?:InkStroke['role']):InkStroke=>({points:[a,b],length:Math.hypot(b[0]-a[0],b[1]-a[1]),role});
  const geometry:{side:'left'|'right';barY:number;numeratorBottom:number;denominatorTop:number;width:number}[]=[];
  const side=(value:FractionEquationVisual['left'],x:number,name:'left'|'right')=>{
    const numerator=inkLine(value.numerator),denominator=inkLine(value.denominator);
    const width=Math.max(numerator.width,denominator.width)+14;
    strokes.push(...placed(value.numerator,x+(width-numerator.width)/2,0));
    strokes.push(segment([x,50],[x+width,50]));
    strokes.push(...placed(value.denominator,x+(width-denominator.width)/2,64));
    geometry.push({side:name,barY:50,numeratorBottom:38,denominatorTop:64,width});
    const suffixOffset=width+(visual.cancelCommonFactor?44:15);
    if(value.suffix)strokes.push(...placed(value.suffix,x+suffixOffset,31));
    if(visual.cancelCommonFactor){
      cancellations.push(segment([x+2,36],[x+width-2,0],'cancel'),segment([x+2,100],[x+width-2,64],'cancel'));
      identities.push(...placed('1',x+width+5,31,'identity'));
    }
    return width+(value.suffix?suffixOffset-width+inkLine(value.suffix).width:0);
  };
  const leftWidth=side(visual.left,0,'left');
  strokes.push(...placed('=',leftWidth+18,31));
  const rightX=leftWidth+65;
  const rightWidth=side(visual.right,rightX,'right');
  // Write the complete factorization before crossing factors out and writing ones.
  strokes.push(...cancellations,...identities);
  return {strokes,width:rightX+rightWidth,height:102,length:strokes.reduce((sum,s)=>sum+s.length,0),geometry};
}
export function pointAt(points: readonly InkPoint[], distance: number): InkPoint {
  for (let i=1;i<points.length;i++) {
    const a=points[i-1],b=points[i],length=Math.hypot(b[0]-a[0],b[1]-a[1]);
    if(distance<=length) { const p=Math.max(0,distance/length);return [a[0]+(b[0]-a[0])*p,a[1]+(b[1]-a[1])*p]; }
    distance-=length;
  }
  return points.at(-1) ?? [0,0];
}
